/**
 * Product Variant Label System
 * Shows dynamic labels (e.g., "Best Value", "Save 20%") on the main product image 
 * when specific variants are selected (e.g., Annual Subscription variants).
 * 
 * Features:
 * - Listens for variant selection changes
 * - Supports Appstle Subscriptions integration
 * - Reads label text from variant metafields
 * - Smooth animations
 * - Responsive design
 */

class ProductVariantLabel {
  constructor() {
    this.init();
  }

  init() {
    // Check if we're on a product page
    if (!document.querySelector('.product__gallery')) {
      return;
    }

    this.setupEventListeners();
    this.checkInitialVariant();
  }

  setupEventListeners() {
    // Listen for theme's native variant change event
    document.addEventListener('variant:change', (event) => {
      this.handleVariantChange(event.detail.variant);
    });

    // Backup: Listen for form changes (Appstle compatibility)
    const variantSelects = document.querySelector('variant-selects');
    if (variantSelects) {
      variantSelects.addEventListener('change', () => {
        // Small delay to allow other scripts to update
        setTimeout(() => {
          this.checkCurrentVariant();
        }, 100);
      });
    }

    // Additional backup: Listen for any form input changes
    const productForm = document.querySelector('[data-type="add-to-cart-form"]');
    if (productForm) {
      productForm.addEventListener('change', () => {
        setTimeout(() => {
          this.checkCurrentVariant();
        }, 150);
      });
    }

    // Appstle specific event listeners
    this.setupAppstleListeners();
  }

  setupAppstleListeners() {
    // Listen for Appstle subscription widget changes
    document.addEventListener('appstle:subscription-widget:changed', () => {
      setTimeout(() => {
        this.checkCurrentVariant();
      }, 200);
    });

    // Monitor for dynamically added Appstle elements
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1 && (
              node.classList?.contains('appstle-subscription-widget') ||
              node.querySelector?.('.appstle-subscription-widget')
            )) {
              this.attachAppstleListeners(node);
            }
          });
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Try to attach to existing Appstle widgets
    const existingWidgets = document.querySelectorAll('.appstle-subscription-widget');
    existingWidgets.forEach(widget => this.attachAppstleListeners(widget));
  }

  attachAppstleListeners(widget) {
    // Listen for changes in Appstle subscription options
    const subscriptionOptions = widget.querySelectorAll('input[type="radio"], select');
    subscriptionOptions.forEach(option => {
      option.addEventListener('change', () => {
        setTimeout(() => {
          this.checkCurrentVariant();
        }, 300);
      });
    });
  }

  checkInitialVariant() {
    // Check if there's an initially selected variant
    setTimeout(() => {
      this.checkCurrentVariant();
    }, 500);
  }

  checkCurrentVariant() {
    // Try to get current variant from various sources
    let currentVariant = this.getCurrentVariant();
    
    if (currentVariant) {
      this.handleVariantChange(currentVariant);
    }
  }

  getCurrentVariant() {
    // Method 1: From data attribute
    const variantData = document.querySelector('[data-selected-variant]');
    if (variantData && variantData.textContent.trim()) {
      try {
        return JSON.parse(variantData.textContent);
      } catch (e) {
        console.warn('Failed to parse variant data:', e);
      }
    }

    // Method 2: From product form
    const productForm = document.querySelector('[data-type="add-to-cart-form"]');
    if (productForm) {
      const variantInput = productForm.querySelector('input[name="id"]');
      if (variantInput && variantInput.value) {
        // Get variant from window.product if available
        if (window.product && window.product.variants) {
          return window.product.variants.find(v => v.id == variantInput.value);
        }
      }
    }

    // Method 3: From variant selects
    const variantSelects = document.querySelector('variant-selects');
    if (variantSelects) {
      const selectedOptions = variantSelects.querySelectorAll('input:checked, select option:checked');
      if (selectedOptions.length > 0 && window.product && window.product.variants) {
        // Try to match variant based on selected options
        const selectedValues = Array.from(selectedOptions).map(option => 
          option.value || option.textContent.trim()
        );
        
        return window.product.variants.find(variant => {
          return selectedValues.every((value, index) => {
            const optionKey = `option${index + 1}`;
            return variant[optionKey] === value;
          });
        });
      }
    }

    return null;
  }

  handleVariantChange(variant) {
    if (!variant) {
      this.hideAllLabels();
      return;
    }

    // Check if this variant should show a label
    const labelText = this.getVariantLabelText(variant);
    
    if (labelText) {
      this.showLabel(labelText);
    } else {
      this.hideAllLabels();
    }
  }

  getVariantLabelText(variant) {
    // Priority 1: Check variant metafields from window.variantMetafields (if available)
    if (window.variantMetafields && window.variantMetafields[variant.id]) {
      const metafield = window.variantMetafields[variant.id];
      if (metafield && metafield.variant_label) {
        return metafield.variant_label;
      }
    }

    // Priority 2: Check for specific variant characteristics
    // Look for "Annual" or "Yearly" in variant title/option values
    const variantText = (
      variant.title + ' ' + 
      (variant.option1 || '') + ' ' + 
      (variant.option2 || '') + ' ' + 
      (variant.option3 || '')
    ).toLowerCase();

    if (variantText.includes('annual') || variantText.includes('yearly')) {
      // Calculate savings if compare_at_price exists
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const savings = variant.compare_at_price - variant.price;
        const percentage = Math.round((savings / variant.compare_at_price) * 100);
        return `Save ${percentage}%`;
      }
      
      // Default label for annual subscriptions
      return 'Best Value';
    }

    // Priority 3: Check product-level metafields
    if (window.productMetafields && window.productMetafields.label && 
        (variantText.includes('annual') || variantText.includes('yearly'))) {
      return window.productMetafields.label;
    }

    return null;
  }

  showLabel(text) {
    const labels = document.querySelectorAll('.product-variant-label');
    
    labels.forEach(label => {
      const textElement = label.querySelector('.product-variant-label__text');
      if (textElement) {
        textElement.textContent = text;
      }
      
      // Show with animation
      label.style.opacity = '0';
      label.style.visibility = 'visible';
      
      // Use requestAnimationFrame for smooth animation
      requestAnimationFrame(() => {
        label.style.opacity = '1';
        label.classList.remove('invisible');
      });
    });
  }

  hideAllLabels() {
    const labels = document.querySelectorAll('.product-variant-label');
    
    labels.forEach(label => {
      label.style.opacity = '0';
      
      // Hide after animation completes
      setTimeout(() => {
        if (label.style.opacity === '0') {
          label.classList.add('invisible');
          label.style.visibility = 'hidden';
        }
      }, 300);
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductVariantLabel();
  });
} else {
  new ProductVariantLabel();
}

// Also initialize on page:loaded event for SPA-like behavior
document.addEventListener('page:loaded', () => {
  new ProductVariantLabel();
});
